import { useCallback, useState } from "react";
import { Link, router, usePage } from "@inertiajs/react";
import { PlusIcon, MegaphoneIcon, PencilSquareIcon } from "@heroicons/react/24/outline";
import { formatDistanceToNow } from "date-fns";
import { Button } from "@components/ui/button";
import { Spinner } from "@components/ui/spinner";
import { ProjectsPagination } from "@components/projects/ProjectsPagination";
import AdPreview from "@components/ads/AdPreview";
import CampaignStatusToggle from "@components/ads/CampaignStatusToggle";
import CreateCampaignModal from "@components/ads/CreateCampaignModal";
import { cn } from "@lib/utils";
import type { PaginationMeta } from "@rails_api_base";

type CampaignStatus = "draft" | "active" | "paused" | "completed";
type FilterStatus = "all" | CampaignStatus;

interface CampaignMini {
  id: number;
  uuid: string;
  name: string | null;
  status: CampaignStatus;
  stage: string;
  created_at: string;
  updated_at: string;
  headlines: Array<{ text: string }>;
  descriptions: Array<{ text: string }>;
  callouts: Array<{ text: string }>;
  website_url: string | null;
  project: {
    uuid: string;
    name: string;
    status: "live" | "paused" | "draft";
    domain: string | null;
    updated_at: string;
  };
}

interface AdsIndexProps {
  campaigns: CampaignMini[];
  pagination: PaginationMeta;
  status_counts: Partial<Record<CampaignStatus, number>>;
  status?: CampaignStatus;
  default_project_uuid: string | null;
  [key: string]: unknown;
}

const FILTERS: { label: string; value: FilterStatus }[] = [
  { label: "All", value: "all" },
  { label: "Active", value: "active" },
  { label: "Paused", value: "paused" },
  { label: "Draft", value: "draft" },
  { label: "Completed", value: "completed" },
];

const STATUS_STYLES: Record<CampaignStatus, string> = {
  active: "bg-success-100 text-success-700",
  paused: "bg-accent-yellow-100 text-accent-yellow-700",
  draft: "bg-neutral-100 text-base-600",
  completed: "bg-neutral-100 text-base-600",
};

const STATUS_LABELS: Record<CampaignStatus, string> = {
  active: "Active",
  paused: "Paused",
  draft: "Draft",
  completed: "Completed",
};

export default function AdsIndex() {
  const { props, url } = usePage<AdsIndexProps>();
  const { campaigns, pagination, status_counts } = props;

  // "Create Ad" opens the picker modal in place instead of navigating to a
  // pre-chosen project. The modal answers "which website? which page?" before
  // creating a campaign and deep-linking the user into the right editor URL.
  const [creating, setCreating] = useState(false);

  const urlParams = new URLSearchParams(url.split("?")[1] ?? "");
  const activeFilter: FilterStatus = (urlParams.get("status") as FilterStatus) ?? "all";

  const totalCount =
    (status_counts.draft ?? 0) +
    (status_counts.active ?? 0) +
    (status_counts.paused ?? 0) +
    (status_counts.completed ?? 0);

  const counts: Record<FilterStatus, number> = {
    all: totalCount,
    active: status_counts.active ?? 0,
    paused: status_counts.paused ?? 0,
    draft: status_counts.draft ?? 0,
    completed: status_counts.completed ?? 0,
  };

  const visit = useCallback((params: Record<string, string | undefined>) => {
    const query: Record<string, string> = {};
    if (params.status && params.status !== "all") query.status = params.status;
    if (params.page && params.page !== "1") query.page = params.page;
    router.get("/ads", query, { preserveScroll: true, preserveState: true });
  }, []);

  const handleFilterChange = (filter: FilterStatus) => {
    visit({ status: filter });
  };

  const handlePageChange = (page: number) => {
    visit({ status: activeFilter, page: String(page) });
  };

  if (totalCount === 0) {
    return (
      <div className="flex flex-col h-full px-8 pt-12">
        <div>
          <h1 className="font-serif text-[28px] font-semibold leading-8 text-base-500">Your Ads</h1>
        </div>
        <EmptyState onCreateAd={() => setCreating(true)} />
        <CreateCampaignModal open={creating} onOpenChange={setCreating} />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full px-8 pt-12">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-serif text-[28px] font-semibold leading-8 text-base-500">Your Ads</h1>
          <p
            data-testid="ads-total-count"
            data-count={pagination.total_count}
            className="mt-2 font-sans text-lg leading-[22px] text-base-400/70"
          >
            {pagination.total_count} total ad{pagination.total_count !== 1 ? "s" : ""}
          </p>
        </div>
        <Button onClick={() => setCreating(true)} data-testid="ads-create-button">
          <PlusIcon className="w-4 h-4" />
          Create Ad
        </Button>
      </div>

      <CreateCampaignModal open={creating} onOpenChange={setCreating} />

      <div className="mt-8 flex items-center">
        <div className="bg-neutral-100 rounded-full p-1 flex items-center">
          {FILTERS.map(({ label, value }) => (
            <button
              key={value}
              onClick={() => handleFilterChange(value)}
              className={cn(
                "rounded-full px-2 py-0.5 flex items-center gap-1 transition-colors",
                activeFilter === value ? "bg-white shadow-sm" : "hover:bg-neutral-200/50"
              )}
            >
              <span
                className={cn(
                  "font-sans text-sm leading-[18px]",
                  activeFilter === value ? "text-base-600" : "text-neutral-600"
                )}
              >
                {label}
              </span>
              <span className="font-sans text-xs leading-4 text-neutral-600">
                ({counts[value]})
              </span>
            </button>
          ))}
        </div>
      </div>

      <div data-testid="ads-list" className="flex flex-col gap-5 mt-6">
        {campaigns.length === 0 ? (
          <div className="py-8 text-center text-base-400">No ads found</div>
        ) : (
          campaigns.map((campaign) => <CampaignCard key={campaign.id} campaign={campaign} />)
        )}
      </div>

      {pagination.total_pages > 1 && (
        <ProjectsPagination pagination={pagination} onPageChange={handlePageChange} />
      )}

      <div className="pb-8" />
    </div>
  );
}

function CampaignCard({ campaign }: { campaign: CampaignMini }) {
  // Address campaigns by their own UUID. The campaign's editor lives at
  // /campaigns/:uuid/:stage; no project scoping needed.
  const editHref = `/campaigns/${campaign.uuid}/content`;
  const [navigating, setNavigating] = useState(false);

  return (
    <div
      data-testid={`campaign-card-${campaign.id}`}
      className="relative flex rounded-2xl border border-neutral-300 overflow-hidden transition-colors hover:border-neutral-400"
    >
      {/* Stretched link — the whole card opens the ad editor. z-10 lifts it
          above AdPreview's `relative` root; the actions column sits at z-20. */}
      <Link
        href={editHref}
        aria-label={`Open ad for ${campaign.project.name}`}
        className="absolute inset-0 z-10"
      />
      <AdPreview
        className="flex-1 rounded-none border-0"
        headlines={campaign.headlines}
        descriptions={campaign.descriptions}
        callouts={campaign.callouts}
        websiteUrl={campaign.website_url ?? undefined}
        emptyLabel={`Unfinished ad for ${campaign.project.name}`}
      />

      <div className="w-[200px] shrink-0 bg-neutral-100 border-l border-neutral-200 flex flex-col justify-between p-5">
        <div className="flex flex-col gap-2">
          <span
            className={cn(
              "rounded-full px-3 py-1 text-xs leading-4 self-start",
              STATUS_STYLES[campaign.status]
            )}
          >
            {STATUS_LABELS[campaign.status]}
          </span>
          <span className="font-sans text-xs leading-4 text-neutral-600 truncate">
            {campaign.project.name}
          </span>
          <span className="font-sans text-xs leading-4 text-neutral-500">
            Edited {formatDistanceToNow(new Date(campaign.updated_at), { addSuffix: true })}
          </span>
        </div>

        <div className="relative z-20 mt-6 flex flex-col gap-2">
          <CampaignStatusToggle campaignId={campaign.id} status={campaign.status} />
          <button
            type="button"
            disabled={navigating}
            onClick={() => {
              setNavigating(true);
              router.visit(editHref, {
                onFinish: () => setNavigating(false),
              });
            }}
            className={cn(
              "bg-white border border-neutral-300 rounded-lg px-3 py-2 text-sm leading-[18px] text-base-500 flex items-center justify-center gap-2 hover:bg-neutral-50 transition-colors",
              navigating && "opacity-60 cursor-not-allowed"
            )}
          >
            {navigating ? (
              <Spinner className="w-4 h-4 text-base-600" />
            ) : (
              <PencilSquareIcon className="w-4 h-4 text-base-600" />
            )}
            Edit Ad
          </button>
        </div>
      </div>
    </div>
  );
}

function EmptyState({ onCreateAd }: { onCreateAd: () => void }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center gap-5">
      <MegaphoneIcon className="w-[120px] h-[120px] text-base-300" strokeWidth={1} />
      <div className="flex flex-col items-center gap-3">
        <h2 className="font-sans text-lg font-semibold leading-[22px] text-base-500">
          No ads yet
        </h2>
        <p className="font-sans text-base leading-5 text-base-300 text-center max-w-[406px]">
          Create a website first, then launch an ad to drive traffic to it.
        </p>
      </div>
      <Button onClick={onCreateAd} data-testid="ads-empty-create-button">
        <PlusIcon className="w-4 h-4" />
        Create Your First Ad
      </Button>
    </div>
  );
}
